import type { Brawler } from '../../../shared/data/brawler';
import type { BrawdleAttributeKey, BrawdleGuessResult } from '../../../shared/types/api';

const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

const getAttributeValue = (brawler: Brawler, key: BrawdleAttributeKey): string => {
  switch (key) {
    case 'rarity':
      return formatValue(brawler.rarity);
    case 'class':
      return formatValue(brawler.class);
    case 'movement':
      return formatValue(brawler.movement);
    case 'range':
      return formatValue(brawler.range);
    case 'reload':
      return formatValue(brawler.reload);
    case 'released':
      return formatValue(brawler.released);
    default:
      return '—';
  }
};

type GuessRowProps = {
  brawler: Brawler;
  result: BrawdleGuessResult;
  attributesOrder: BrawdleAttributeKey[];
  attributeLabels: Record<BrawdleAttributeKey, string>;
  showReleasedHint: boolean;
};

const getArrowSymbol = (status: string): string | null => {
  if (status === 'higher') {
    return '▲';
  }
  if (status === 'lower') {
    return '▼';
  }
  return null;
};

export const GuessRow = ({
  brawler,
  result,
  attributesOrder,
  attributeLabels,
  showReleasedHint,
}: GuessRowProps) => (
  <div className="guess-row rounded-2xl border border-neutral-700 bg-neutral-900/70 p-4">
    <div className="grid grid-cols-[56px_1fr_auto] items-center gap-3">
      <img
        src={brawler.imagePath ?? undefined}
        alt={brawler.name}
        className="h-14 w-14 rounded-2xl border border-white/10 bg-slate-900 object-cover"
      />
      <h3 className="text-center text-lg font-semibold text-white">{brawler.name}</h3>
      <span className="text-sm text-white">{brawler.rarity}</span>
    </div>
    <div className="guess-row__badges grid grid-cols-2 gap-2 sm:grid-cols-3">
      {attributesOrder.map((key) => {
        const status = result[key];
        const badgeClass = status === 'correct' ? 'badge--correct' : 'badge--wrong';
        const arrow = showReleasedHint && key === 'released' ? getArrowSymbol(status) : null;
        return (
          <span key={key} className={`badge ${badgeClass}`}>
            <span className="text-xs uppercase tracking-wide text-white">
              {attributeLabels[key]}
            </span>
            <strong className="flex items-center gap-2 text-base">
              {getAttributeValue(brawler, key)}
              {arrow && (
                <span className="guess-arrow" aria-hidden>
                  {arrow}
                </span>
              )}
            </strong>
          </span>
        );
      })}
    </div>
  </div>
);

